import { Linkedin, Instagram, Twitter, Youtube } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "/communiti-logo.png";

const COL_LINKS = [
    { label: "Home", href: "/" },
    { label: "Services", href: "/services" },
    { label: "Blogs", href: "/blog" },
    { label: "About", href: "/about" },
    { label: "Insights", href: "/#insights" },
    { label: "Contact", href: "/#contact" },
];

const SOCIALS = [
    {
        label: "LinkedIn",
        href: "https://www.linkedin.com/company/commmmunitimedia-marketing/posts/?feedView=all",
        icon: Linkedin,
    },
    {
        label: "Instagram",
        href: "https://www.instagram.com/commmmuniti/",
        icon: Instagram,
    },
    {
        label: "Twitter",
        href: "https://x.com/commmmunitimm",
        icon: Twitter,
    },
    {
        label: "Pinterest",
        href: "https://in.pinterest.com/commmmuniti",
        icon: (props) => (
            <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
                <path d="M12 2C6.48 2 2 6.48 2 12c0 4.08 2.45 7.59 5.96 9.12-.08-.78-.16-1.97.03-2.82.18-.76 1.15-4.85 1.15-4.85s-.29-.59-.29-1.46c0-1.37.79-2.39 1.78-2.39.84 0 1.25.63 1.25 1.39 0 .85-.54 2.11-.82 3.28-.23.98.49 1.78 1.46 1.78 1.75 0 3.1-1.85 3.1-4.51 0-2.36-1.7-4.01-4.12-4.01-2.81 0-4.46 2.11-4.46 4.29 0 .85.33 1.76.74 2.25.08.1.09.19.07.29-.08.32-.25 1-.28 1.14-.04.18-.15.22-.34.13-1.26-.59-2.05-2.42-2.05-3.9 0-3.17 2.3-6.08 6.65-6.08 3.49 0 6.2 2.49 6.2 5.81 0 3.47-2.19 6.26-5.22 6.26-1.02 0-1.98-.53-2.31-1.16l-.63 2.4c-.23.88-.85 1.98-1.27 2.65.95.29 1.96.45 3.01.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
            </svg>
        ),
    },
    {
        label: "Google My Business",
        href: "https://share.google/fx4zbUCZMMgVciS5H",
        icon: (props) => (
            <svg viewBox="0 0 24 24" {...props}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
        ),
    },
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-white text-communiti-text pt-16 pb-10 border-t border-communiti-line"
        >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                <div className="lg:col-span-4">
                    <img
                        src={LOGO_URL}
                        alt="Commmmuniti Media Marketing"
                        className="h-12 w-auto object-contain"
                    />
                    <p className="mt-5 text-sm text-communiti-muted max-w-xs leading-relaxed">
                        A growth studio for brands that refuse to look or sound like
                        everyone else. Commmmuniti — the best digital marketing agency in
                        Indore.
                    </p>
                </div>

                <div className="lg:col-span-5 grid grid-cols-3 gap-4 text-sm">
                    {COL_LINKS.map((l) =>
                        l.href.startsWith("/") && !l.href.includes("#") ? (
                            <Link
                                key={l.label}
                                to={l.href}
                                data-testid={`footer-link-${l.label.toLowerCase()}`}
                                className="text-communiti-muted hover:text-communiti"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.label}
                                href={l.href}
                                data-testid={`footer-link-${l.label.toLowerCase()}`}
                                className="text-communiti-muted hover:text-communiti"
                            >
                                {l.label}
                            </a>
                        )
                    )}
                </div>

                <div className="lg:col-span-3 flex flex-col gap-4 lg:items-end">
                    <div className="flex items-center gap-3">
                        {SOCIALS.map((s) => {
                            const Icon = s.icon;
                            return (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`social-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-communiti-line hover:border-communiti hover:text-communiti transition-colors"
                                    aria-label={s.label}
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            );
                        })}
                    </div>
                    <a
                        href="tel:+917987924110"
                        className="text-sm text-communiti-muted hover:text-communiti"
                    >
                        +91 7987924110
                    </a>
                    <a
                        href="mailto:commmmuniti@gmail.com"
                        className="text-sm text-communiti-muted hover:text-communiti break-all"
                    >
                        commmmuniti@gmail.com
                    </a>
                </div>
            </div>

            <div className="mx-auto max-w-[1440px] px-6 lg:px-10 mt-12 pt-6 border-t border-communiti-line text-xs text-communiti-muted">
                <p>© {new Date().getFullYear()} <span className="brand-name">Commmmuniti</span> Media Marketing. All rights reserved.</p>
            </div>
        </footer>
    );
}
